import React from 'react';

import { ContentItem } from '@/core/ambient-intelligence/types/ambient-intelligence.types';

import { AISourceIndicator, AISourceIndicatorMinimal } from '../ui/AISourceIndicator';

// Props interface for content renderers
export interface ContentItemProps {
  content: ContentItem;
  variant?: 'card' | 'expanded' | 'minimal';
  onAction?: (action: string, content: ContentItem, data?: any) => void;
  className?: string;
}

// Default content renderer for unknown types
const DefaultContentRenderer: React.FC<ContentItemProps> = ({ content, className }) => {
  return (
    <div className={`inspect-theme-bg-elevated p-4 rounded-lg ${className}`}>
      <h3 className="inspect-theme-text font-medium mb-2">{content.title}</h3>
      {content.description && <p className="inspect-theme-text-muted text-sm">{content.description}</p>}
      {content.tags && content.tags.length > 0 && (
        <div className="mt-2 flex gap-2">
          {content.tags.map((tag, index) => (
            <span key={index} className="inspect-theme-tag text-xs px-2 py-1 rounded">
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * ContentRegistry - Maps content types to their visual renderers
 * Extensible system that allows new content types without core changes
 */
class ContentRegistryClass {
  private renderers = new Map<string, React.ComponentType<ContentItemProps>>();

  /**
   * Register a new content type renderer
   */
  register(contentType: string, renderer: React.ComponentType<ContentItemProps>): void {
    this.renderers.set(contentType, renderer);
    console.log(`✅ Registered content renderer: ${contentType}`);
  }

  /**
   * Get renderer for a content type
   */
  getRenderer(contentType: string): React.ComponentType<ContentItemProps> {
    return this.renderers.get(contentType) || DefaultContentRenderer;
  }

  /**
   * Render any content item
   */
  render(content: ContentItem, props?: Omit<ContentItemProps, 'content'>): React.ReactElement {
    // First try to get renderer by content type
    let Renderer = this.renderers.get(content.type);

    // If not found and content has a cardType in data, try that
    if (!Renderer && content.data?.cardType) {
      Renderer = this.renderers.get(content.data.cardType as string);
    }

    // Fall back to default renderer
    if (!Renderer) {
      Renderer = DefaultContentRenderer;
    }

    const renderedContent = <Renderer content={content} {...props} />;

    // Skip AI source indicator for share-suggestions and conversation cards
    const skipIndicator = content.data?.cardType === 'share-suggestions' || content.type === 'conversation';

    // If we should skip the indicator or there's no source, return the content as-is
    if (skipIndicator || !content.data?.source) {
      return renderedContent;
    }

    // Apply appropriate AI source indicator based on variant
    if (props?.variant === 'minimal') {
      return (
        <AISourceIndicatorMinimal source={content.data.source}>{renderedContent}</AISourceIndicatorMinimal>
      );
    }

    // For card and expanded variants, or when variant is not specified
    return <AISourceIndicator source={content.data.source}>{renderedContent}</AISourceIndicator>;
  }

  /**
   * List all registered types
   */
  getRegisteredTypes(): string[] {
    return Array.from(this.renderers.keys());
  }

  /**
   * Check if a content type has a registered renderer
   */
  hasRenderer(contentType: string): boolean {
    return this.renderers.has(contentType);
  }

  /**
   * Unregister a content type (useful for testing)
   */
  unregister(contentType: string): void {
    this.renderers.delete(contentType);
  }

  /**
   * Clear all registrations (useful for testing)
   */
  clear(): void {
    this.renderers.clear();
  }
}

// Export singleton instance
export const ContentRegistry = new ContentRegistryClass();

// Export types
export type { ContentItemProps };
